import { getGitDate } from '../utils/buildVersion' with { type: 'macro' };
import { BUILD_PLATFORM, IS_FILE, IS_TAURI } from './platformUtils';
import { relaunch } from '@tauri-apps/plugin-process';
import { check } from '@tauri-apps/plugin-updater';
import { toast } from 'react-toastify';

const buildDate = new Date(await getGitDate());

const CHECK_URL =
  process.env.NODE_ENV === 'production'
    ? 'https://logbook.shoghisimon.ca/date'
    : 'http://localhost:3080/date';

const reloadChannel = new BroadcastChannel('reload_channel');
reloadChannel.onmessage = event => {
  if (event.data === 'reload') {
    window.location.reload();
  }
};

async function isTauriOutdated(): Promise<boolean> {
  return !!(await check().catch(() => false));
}

/**
 * Checks if the current build is up to date by comparing the build date with the date from the server.
 * @returns Returns true if there is a newer version available, false otherwise.
 */
export async function isVersionOutdated(): Promise<boolean> {
  if (BUILD_PLATFORM === 'tauri') {
    if (await isTauriOutdated()) return true;
  }
  try {
    const response = await fetch(CHECK_URL);

    if (!response.ok) {
      console.error(
        'Failed to fetch version information:',
        response.statusText,
      );
      return false;
    }

    const serverDateString = await response.text();
    const serverDate = new Date(serverDateString);

    if (isNaN(serverDate.getTime())) {
      console.error(
        'Invalid date format received from server:',
        serverDateString,
      );
      return false;
    }

    return serverDate > buildDate;
  } catch (error) {
    console.error('Error checking version:', error);
    return false;
  }
}

async function updateWeb() {
  const reloadChannel = new BroadcastChannel('reload_channel');
  reloadChannel.postMessage('reload');
  reloadChannel.close();
}

async function updateFile() {
  const baseUrl =
    process.env.NODE_ENV === 'production'
      ? 'https://logbook.shoghisimon.ca'
      : 'http://localhost:3080';
  const indexUrl = `${baseUrl}/static/index.html`;
  const officerUrl = `${baseUrl}/static/officer.html`;

  // Download both files
  const [indexResponse, officerResponse] = await Promise.all([
    fetch(indexUrl),
    fetch(officerUrl),
  ]);

  if (!indexResponse.ok || !officerResponse.ok) {
    console.error(
      'Failed to fetch new version files:',
      indexResponse.statusText,
      officerResponse.statusText,
    );
    return;
  }

  const indexBlob = await indexResponse.blob();
  const officerBlob = await officerResponse.blob();

  // Create object URLs for the downloaded files
  const indexObjectUrl = URL.createObjectURL(indexBlob);
  const officerObjectUrl = URL.createObjectURL(officerBlob);

  // Create a temporary link to trigger the download of both files
  const link = document.createElement('a');
  link.style.display = 'none';
  document.body.appendChild(link);

  // Trigger download for index.html
  link.href = indexObjectUrl;
  link.download = 'index.html';
  link.click();

  // Trigger download for officer.html
  link.href = officerObjectUrl;
  link.download = 'officer.html';
  link.click();

  // Clean up
  URL.revokeObjectURL(indexObjectUrl);
  URL.revokeObjectURL(officerObjectUrl);
  document.body.removeChild(link);
}

async function updateTauri() {
  const update = await check().catch(() => false as false);
  if (!update) {
    toast.error("Error updating: couldn't find update.");
    return;
  }

  const updateToast = toast.info(
    `Found update ${update.version} from ${update.date}`,
    {
      autoClose: false,
    },
  );

  console.log(`found update ${update.version} from ${update.date}`);
  let downloaded = 0;
  let contentLength = 0;
  // alternatively we could also call update.download() and update.install() separately
  await update.downloadAndInstall(event => {
    switch (event.event) {
      case 'Started':
        if (!event.data.contentLength) {
          toast.update(updateToast, {
            type: 'error',
          });

          console.warn('Content length is ', event.data.contentLength);
          return;
        }
        contentLength = event.data.contentLength;
        toast.update(updateToast, {
          render: `Started downloading ${event.data.contentLength} bytes`,
          progress: 0,
        });
        break;
      case 'Progress':
        downloaded += event.data.chunkLength;
        toast.update(updateToast, {
          render: `Downloaded ${((downloaded / contentLength) * 100).toFixed(2)}%`,
          progress: downloaded / contentLength,
        });
        console.log(`downloaded ${downloaded} from ${contentLength}`);
        break;
      case 'Finished':
        toast.update(updateToast, {
          type: 'success',
          render: `Downloaded! Restart to apply update.`,
          onClick: () => relaunch(),
          autoClose: false,
          closeOnClick: true,
          progress: undefined,
        });
        console.log('download finished');
        break;
    }
  });

  console.log('update installed');
}

export async function getNewUpdate() {
  if (IS_FILE) {
    await updateFile();
    return;
  }
  if (IS_TAURI) {
    await updateTauri();
    return;
  }
  await updateWeb();
}

export function checkForVersionUpdate() {
  isVersionOutdated().then(outdated => {
    if (outdated) {
      toast.info(
        'A new version of the logbook is available! Click here to update.',
        {
          onClick: getNewUpdate,
          autoClose: false,
          closeOnClick: true,
        },
      );
    }
  });
}
